import { TableText } from "@/components/table-tools";
import { useInfiniteQuery } from "@connectrpc/connect-query";

import { listEvents } from "@/gen/nodebase/v1/event-EventService_connectquery";
import { Severity } from "@/gen/nodebase/v1/event_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { AccountScope } from "@/components/scope";
import { enumLabel, fmtDateTime } from "@/lib/format";

const PAGE_SIZE = 50;

function severityTone(label: string): "destructive" | "warning" | "secondary" {
  if (label === "critical") return "destructive";
  if (label === "warning") return "warning";
  return "secondary";
}

export function Events() {
  // ListEvents is a forward-only cursor (PageRequest/PageResponse), which is
  // exactly what an infinite query wants: pages accumulate and there is no page
  // count to show, because the server does not know one either.
  const events = useInfiniteQuery(
    listEvents,
    { page: { limit: PAGE_SIZE } },
    {
      pageParamKey: "page",
      getNextPageParam: (last) =>
        last.page?.nextCursor
          ? { afterCursor: last.page.nextCursor, limit: PAGE_SIZE }
          : undefined,
    },
  );

  const rows = events.data?.pages.flatMap((p) => p.events) ?? [];

  return (
    <>
      <PageHeader title="Events" scope={<AccountScope noun="Events" />} />
      <p className="mb-4 text-sm text-muted-foreground">
        Account activity and Worker or device changes. For a task's output and
        logs, ask your AI to inspect that task on the Worker.
      </p>
      <ErrorNote error={events.error} />

      {events.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyNote>
          No events yet. Workers and devices report here as they come and go.
        </EmptyNote>
      ) : (
        <>
          <Table aria-label="Events">
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Payload</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((ev) => {
                const sev = enumLabel(Severity, ev.severity);
                return (
                  <TableRow key={ev.id} data-testid="event-row">
                    <TableCell className="whitespace-nowrap">
                      {fmtDateTime(ev.createdUnix)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={severityTone(sev)}>{sev}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{ev.type}</TableCell>
                    <TableCell>{ev.source}</TableCell>
                    <TableCell>
                      <div className="font-mono text-xs text-muted-foreground">
                        <TableText value={ev.payloadJson} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {events.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                data-testid="events-more"
                disabled={events.isFetchingNextPage}
                onClick={() => events.fetchNextPage()}
              >
                {events.isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
